// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef, useEffect, useCallback } from 'react';
import type { StepComponentProps } from '../../types/wizard';
import { toIsoDate } from '../../utils/date';
import { PENSION_TYPE_KEYS, INVESTMENT_TYPE_KEYS, POLICY_TYPE_LABELS } from '../../types/wizard';
import type { PolicyTypeKey } from '../../types/wizard';
import type { RejectionReasonOption, ProviderRejectionRequest } from '../../types/providerAccess';
import { RejectionPanel } from '../wizard/RejectionPanel';

const POLICY_CATEGORY_OPTIONS = [
  { value: 'pension', text: 'Pensions' },
  { value: 'investment', text: 'Investments' },
];

const POLICY_STATUS_OPTIONS = [
  { value: 'ACTIVE', text: 'Active' },
  { value: 'CLOSED', text: 'Closed' },
];

const CLOSED_REASON_OPTIONS = [
  { value: 'RETIRED', text: 'Retired' },
  { value: 'TRANSFERRED_OUT', text: 'Transferred out' },
];

/** Reverse-map from display label to PolicyTypeKey */
const LABEL_TO_KEY: Record<string, PolicyTypeKey> = Object.entries(POLICY_TYPE_LABELS).reduce(
  (acc, [key, label]) => ({ ...acc, [label]: key as PolicyTypeKey }),
  {} as Record<string, PolicyTypeKey>,
);

/**
 * Transfer matrix: given the ORIGINAL policy type, what can it be transferred TO?
 * ARF -> ARF only
 * Vested PRSA -> Vested PRSA only
 * PRB -> PRB, Defined Contribution, Executive Pension, Mastertrust
 * PRSA (Standard/Non-Standard/AVC) -> Standard PRSA, Non-Standard PRSA, PRSA AVC, Defined Contribution, Mastertrust, Executive Pension
 * Personal Pension -> PRSA (all), Personal Pension
 * Company Pension (DC, Exec, Mastertrust, Director's) -> PRB, PRSA (all), Defined Contribution, Mastertrust, Executive Pension
 */
const TRANSFER_MATRIX: Record<string, PolicyTypeKey[]> = {
  ARF: ['ARF'],
  VESTED_PRSA: ['VESTED_PRSA'],
  PRB_BOB: ['PRB_BOB', 'DEFINED_CONTRIBUTION', 'EXECUTIVE_PENSION', 'MASTERTRUST'],
  PRSA_STANDARD: ['PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'DEFINED_CONTRIBUTION', 'MASTERTRUST', 'EXECUTIVE_PENSION'],
  PRSA_NON_STANDARD: ['PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'DEFINED_CONTRIBUTION', 'MASTERTRUST', 'EXECUTIVE_PENSION'],
  PRSA_AVC: ['PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'DEFINED_CONTRIBUTION', 'MASTERTRUST', 'EXECUTIVE_PENSION'],
  PERSONAL_PENSION: ['PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'PERSONAL_PENSION'],
  DEFINED_CONTRIBUTION: ['PRB_BOB', 'PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'DEFINED_CONTRIBUTION', 'MASTERTRUST', 'EXECUTIVE_PENSION'],
  EXECUTIVE_PENSION: ['PRB_BOB', 'PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'DEFINED_CONTRIBUTION', 'MASTERTRUST', 'EXECUTIVE_PENSION'],
  MASTERTRUST: ['PRB_BOB', 'PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'DEFINED_CONTRIBUTION', 'MASTERTRUST', 'EXECUTIVE_PENSION'],
  DIRECTORS_PENSION: ['PRB_BOB', 'PRSA_STANDARD', 'PRSA_NON_STANDARD', 'PRSA_AVC', 'DEFINED_CONTRIBUTION', 'MASTERTRUST', 'EXECUTIVE_PENSION'],
};

/** Get allowed transfer-to options based on original policy type name */
function getTransferPolicyTypeOptions(originalPolicyTypeName: string | null | undefined): { value: string; text: string }[] {
  if (!originalPolicyTypeName) {
    // Fallback: show all pension policy types
    return PENSION_TYPE_KEYS.map((key) => ({ value: key, text: POLICY_TYPE_LABELS[key] }));
  }
  const originalKey = LABEL_TO_KEY[originalPolicyTypeName];
  if (!originalKey) {
    return PENSION_TYPE_KEYS.map((key) => ({ value: key, text: POLICY_TYPE_LABELS[key] }));
  }
  const allowed = TRANSFER_MATRIX[originalKey];
  if (!allowed) {
    return PENSION_TYPE_KEYS.map((key) => ({ value: key, text: POLICY_TYPE_LABELS[key] }));
  }
  return allowed.map((key) => ({ value: key, text: POLICY_TYPE_LABELS[key] }));
}

/** Retired can only go to ARF / Vested PRSA */
const RETIRED_POLICY_TYPE_OPTIONS = [
  { value: 'ARF', text: 'ARF' },
  { value: 'VESTED_PRSA', text: 'Vested PRSA' },
];

const TRANSFER_TYPE_OPTIONS = [
  { value: 'CASH', text: 'Cash' },
  { value: 'IN_SPECIE', text: 'In-specie' },
];

/** Strip everything except digits and dot, keep only first dot */
function toRawNumber(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

/** Format a raw numeric string as 1,234.56 for display */
function formatEuro(raw: string): string {
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = parts.length > 1 ? '.' + parts[1] : '';
  return `${formatted}${dec}`;
}

/** On blur, ensure 2 decimal places */
function formatEuroBlur(raw: string): string {
  if (!raw) return '';
  const num = parseFloat(raw);
  if (isNaN(num)) return '';
  return num.toFixed(2);
}

interface StepLoaValidationProps extends StepComponentProps {
  rejectionReasons: RejectionReasonOption[];
  rejectSubmitting: boolean;
  onReject: (request: ProviderRejectionRequest) => Promise<boolean>;
  brokerageName?: string;
  policyTypeName?: string | null;
  policyTypeCategory?: string | null;
  clientName?: string | null;
  advisorName?: string | null;
  /** When true, hide the Policy Details sub-section shown after "Yes". */
  hidePolicyDetails?: boolean;
  /** When true, show ONLY the Policy Details section (no LOA gate). */
  policyOnly?: boolean;
}

export function StepLoaValidation({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
  rejectionReasons,
  rejectSubmitting,
  onReject,
  brokerageName,
  policyTypeName,
  policyTypeCategory,
  clientName,
  advisorName,
  hidePolicyDetails = false,
  policyOnly = false,
}: StepLoaValidationProps) {
  const loaAccepted = data.loaAccepted as boolean | undefined;
  const [showPolicyNumberInfo, setShowPolicyNumberInfo] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showPolicyNumberInfo) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setShowPolicyNumberInfo(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPolicyNumberInfo]);

  const categoryLabel = policyTypeCategory
    ? policyTypeCategory.charAt(0).toUpperCase() + policyTypeCategory.slice(1).toLowerCase()
    : 'policy';

  const buildDescription = () => {
    if (advisorName && brokerageName && clientName) {
      return `${advisorName} at ${brokerageName} is requesting ${categoryLabel.toLowerCase()} details for ${clientName}. You can view the Letter of Authority in the email sent to you by ${brokerageName}.`;
    }
    if (brokerageName) {
      return `${brokerageName} is requesting ${categoryLabel.toLowerCase()} details. You can view the Letter of Authority in the email sent to you by ${brokerageName}.`;
    }
    return 'Please review the Letter of Authority sent to you via email.';
  };

  const isClosed = (data.policyStatus as string) === 'CLOSED';
  const closedReason = (data.policyClosedReason as string) || '';
  const isRetired = isClosed && closedReason === 'RETIRED';
  const closedPolicyTypeOptions = isRetired ? RETIRED_POLICY_TYPE_OPTIONS : getTransferPolicyTypeOptions(policyTypeName);
  const closedLabel = isRetired ? 'retired' : 'transferred';

  const handleCurrencyChange = useCallback((field: string, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
    onClearFieldError(field);
  }, [onFieldChange, onClearFieldError]);

  const handleCurrencyBlur = useCallback((field: string) => {
    const raw = (data[field] as string) || '';
    if (raw) onFieldChange(field, formatEuroBlur(raw));
  }, [data, onFieldChange]);

  const getCurrencyDisplay = (field: string): string => {
    const raw = (data[field] as string) || '';
    return raw ? formatEuro(raw) : '';
  };

  const chevronIcon = (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const calendarIcon = (maskId: string) => (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M16 0H0V16H16V0Z" fill="white"/>
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
        <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
        <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
        <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
      </g>
    </svg>
  );

  const selectClass = (field: string) =>
    `w-full h-[48px] px-md pr-xxl rounded-lg border ${fieldErrors[field] ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data[field] as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`;

  const inputClass = (field: string) =>
    `w-full h-[48px] px-md rounded-lg border ${fieldErrors[field] ? 'border-[#E7000B]' : 'border-grey-04'} bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`;

  const dateInputClass = (field: string) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border ${fieldErrors[field] ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data[field] as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

  const renderFieldError = (field: string) =>
    fieldErrors[field] ? (
      <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{fieldErrors[field]}</div>
    ) : null;

  const renderSelectField = (field: string, label: string, options: { value: string; text: string }[], placeholder: string) => (
    <div className="flex flex-col gap-sm">
      <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
        {label} <span className="text-[#E7000B]">*</span>
      </div>
      <div className="relative">
        <select
          value={(data[field] as string) || ''}
          onChange={(e) => { onFieldChange(field, e.target.value); onClearFieldError(field); }}
          className={selectClass(field)}
        >
          <option value="" disabled className="text-[#B6B6B6]">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
          ))}
        </select>
        {chevronIcon}
      </div>
      {renderFieldError(field)}
    </div>
  );

  const renderTextField = (field: string, label: string, placeholder?: string) => (
    <div className="flex flex-col gap-sm">
      <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
        {label} <span className="text-[#E7000B]">*</span>
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={(data[field] as string) || ''}
        onChange={(e) => { onFieldChange(field, e.target.value); onClearFieldError(field); }}
        className={inputClass(field)}
      />
      {renderFieldError(field)}
    </div>
  );

  const renderDateField = (field: string, label: string, maskId: string) => (
    <div className="flex flex-col gap-sm">
      <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
        {label} <span className="text-[#E7000B]">*</span>
      </div>
      <div className="relative">
        <input
          type="date"
          value={(data[field] as string) || ''}
          onChange={(e) => { onFieldChange(field, toIsoDate(e.target.value)); onClearFieldError(field); }}
          className={dateInputClass(field)}
        />
        {calendarIcon(maskId)}
      </div>
      {renderFieldError(field)}
    </div>
  );

  const renderCurrencyField = (field: string, label: string) => (
    <div className="flex flex-col gap-sm">
      <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
        {label} <span className="text-[#E7000B]">*</span>
      </div>
      <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${fieldErrors[field] ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
        <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
        <input
          type="text"
          inputMode="decimal"
          value={getCurrencyDisplay(field)}
          onChange={(e) => handleCurrencyChange(field, e.target.value)}
          onBlur={() => handleCurrencyBlur(field)}
          className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
        />
      </div>
      {renderFieldError(field)}
    </div>
  );

  return (
    <div className="flex flex-col">
      {!policyOnly && (
        <>
          <h2 className="text-black font-sans text-h5 font-normal leading-normal not-italic">
            Letter of Authority
          </h2>

          <div className="mt-sm text-black font-sans text-body-02 font-normal leading-normal not-italic">
            {buildDescription()}
          </div>

          <div className="mt-lg text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Is the Letter of Authority valid?
          </div>
        </>
      )}

      {!policyOnly && (
      <div className="mt-md flex flex-col gap-md">
        <label className="flex items-center gap-[12px] cursor-pointer" onClick={() => onFieldChange('loaAccepted', true)}>
          <div className="w-[16px] h-[16px] rounded-full border border-purple-primary flex items-center justify-center bg-white shrink-0">
            {loaAccepted === true && (
              <div className="w-[8px] h-[8px] rounded-full bg-purple-primary" />
            )}
          </div>
          <div className="text-black font-sans text-body-02 font-normal leading-normal not-italic">Yes</div>
        </label>

        <label className="flex items-center gap-[12px] cursor-pointer" onClick={() => onFieldChange('loaAccepted', false)}>
          <div className="w-[16px] h-[16px] rounded-full border border-purple-primary flex items-center justify-center bg-white shrink-0">
            {loaAccepted === false && (
              <div className="w-[8px] h-[8px] rounded-full bg-purple-primary" />
            )}
          </div>
          <div className="text-black font-sans text-body-02 font-normal leading-normal not-italic">No</div>
        </label>
      </div>
      )}

      {!policyOnly && loaAccepted === false && (
        <RejectionPanel
          rejectionReasons={rejectionReasons}
          submitting={rejectSubmitting}
          onReject={onReject}
        />
      )}


      {(policyOnly || (loaAccepted === true && !hidePolicyDetails)) && (
        <>
          {/* Divider (only when shown alongside the LOA gate) */}
          {!policyOnly && <div className="mt-lg w-[888px] h-[1px] bg-[#E1E1E1]" />}

          {/* Policy Details section */}
          <div className={`${policyOnly ? '' : 'mt-lg'} flex flex-col gap-lg`}>
            <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
              {policyOnly ? 'Policy Info' : 'Policy Details'}
            </div>

            {/* Row 1: Policy number + Type of policy + Pension Type */}
            <div className="flex flex-row gap-lg items-end">
              <div className="flex flex-col flex-1">
                <div className="flex flex-col gap-sm flex-1 justify-end">
                  <div className="flex items-center justify-between gap-sm">
                    <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                      Policy number / Plan Reference No / Member Reference No <span className="text-[#E7000B]">*</span>
                    </div>
                    <div className="relative shrink-0" ref={infoRef}>
                      <button
                        type="button"
                        onClick={() => setShowPolicyNumberInfo((prev) => !prev)}
                        className="cursor-pointer bg-transparent border-none p-0 flex items-center"
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <mask id="mask_info_loa" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                            <path d="M20 0H0V20H20V0Z" fill="white"/>
                          </mask>
                          <g mask="url(#mask_info_loa)">
                            <path d="M9.99967 1.04183C14.9413 1.04183 18.958 5.0585 18.958 10.0002C18.958 14.9418 14.9413 18.9585 9.99967 18.9585C5.05801 18.9585 1.04134 14.9418 1.04134 10.0002C1.04134 5.0585 5.05801 1.04183 9.99967 1.04183ZM9.99967 17.7085C14.2497 17.7085 17.708 14.2502 17.708 10.0002C17.708 5.75016 14.2497 2.29183 9.99967 2.29183C5.74967 2.29183 2.29134 5.75016 2.29134 10.0002C2.29134 14.2502 5.74967 17.7085 9.99967 17.7085Z" fill="#141414"/>
                            <path d="M10 8.54183C10.3417 8.54183 10.625 8.82516 10.625 9.16683V13.3335C10.625 13.6752 10.3417 13.9585 10 13.9585C9.65833 13.9585 9.375 13.6752 9.375 13.3335V9.16683C9.375 8.82516 9.65833 8.54183 10 8.54183Z" fill="#141414"/>
                            <path d="M9.99967 5.83262C10.108 5.83262 10.2163 5.85762 10.3163 5.89928C10.4163 5.94095 10.508 5.99928 10.5913 6.07428C10.6663 6.15762 10.7247 6.24095 10.7663 6.34928C10.808 6.44928 10.833 6.55762 10.833 6.66595C10.833 6.77428 10.808 6.88262 10.7663 6.98262C10.7247 7.08262 10.6663 7.17428 10.5913 7.25762C10.508 7.33262 10.4163 7.39095 10.3163 7.43262C10.1163 7.51595 9.88301 7.51595 9.68301 7.43262C9.58301 7.39095 9.49134 7.33262 9.40801 7.25762C9.33301 7.17428 9.27467 7.08262 9.23301 6.98262C9.19134 6.88262 9.16634 6.77428 9.16634 6.66595C9.16634 6.55762 9.19134 6.44928 9.23301 6.34928C9.27467 6.24095 9.33301 6.15762 9.40801 6.07428C9.49134 5.99928 9.58301 5.94095 9.68301 5.89928C9.78301 5.85762 9.89134 5.83262 9.99967 5.83262Z" fill="#141414"/>
                          </g>
                        </svg>
                      </button>
                      {showPolicyNumberInfo && (
                        <div className="absolute right-0 top-[28px] z-10 w-[280px] px-[12px] py-sm rounded-lg bg-black text-white font-sans text-[13px] font-normal leading-normal shadow-lg">
                          This number may be referred to by various names depending on the provider
                        </div>
                      )}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={(data.fullPolicyNumber as string) || ''}
                    onChange={(e) => {
                      onFieldChange('fullPolicyNumber', e.target.value);
                      onClearFieldError('fullPolicyNumber');
                    }}
                    className={inputClass('fullPolicyNumber')}
                  />
                </div>
                {fieldErrors.fullPolicyNumber && (
                  <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal mt-sm">{fieldErrors.fullPolicyNumber}</div>
                )}
              </div>

              <div className="flex flex-col flex-1">
                <div className="flex flex-col gap-sm flex-1 justify-end">
                  <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                    Type of policy <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <select
                      value={(data.policyCategory as string) || ''}
                      onChange={(e) => {
                        const newCategory = e.target.value;
                        onFieldChange('policyCategory', newCategory);
                        onClearFieldError('policyCategory');
                        // Clear the opposing type field when category changes
                        if (newCategory === 'investment') {
                          onFieldChange('pensionType', '');
                        } else {
                          onFieldChange('investmentType', '');
                        }
                      }}
                      className={selectClass('policyCategory')}
                    >
                      <option value="" disabled className="text-[#B6B6B6]">Select type of policy...</option>
                      {POLICY_CATEGORY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                      ))}
                    </select>
                    {chevronIcon}
                  </div>
                </div>
                {fieldErrors.policyCategory && (
                  <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal mt-sm">{fieldErrors.policyCategory}</div>
                )}
              </div>

              {(data.policyCategory as string) === 'investment' ? (
                <div className="flex flex-col flex-1">
                  <div className="flex flex-col gap-sm flex-1 justify-end">
                    <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                      Investment Type <span className="text-[#E7000B]">*</span>
                    </div>
                    <div className="relative">
                      <select
                        value={(data.investmentType as string) || ''}
                        onChange={(e) => {
                          onFieldChange('investmentType', e.target.value);
                          onClearFieldError('investmentType');
                        }}
                        className={selectClass('investmentType')}
                      >
                        <option value="" disabled className="text-[#B6B6B6]">Select investment type...</option>
                        {INVESTMENT_TYPE_KEYS.map((key) => (
                          <option key={key} value={key} className="text-[#333]">{POLICY_TYPE_LABELS[key]}</option>
                        ))}
                      </select>
                      {chevronIcon}
                    </div>
                  </div>
                  {fieldErrors.investmentType && (
                    <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal mt-sm">{fieldErrors.investmentType}</div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col flex-1">
                  <div className="flex flex-col gap-sm flex-1 justify-end">
                    <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                      Pension Type <span className="text-[#E7000B]">*</span>
                    </div>
                    <div className="relative">
                      <select
                        value={(data.pensionType as string) || ''}
                        onChange={(e) => {
                          onFieldChange('pensionType', e.target.value);
                          onClearFieldError('pensionType');
                        }}
                        className={selectClass('pensionType')}
                      >
                        <option value="" disabled className="text-[#B6B6B6]">Select pension type...</option>
                        {PENSION_TYPE_KEYS.map((key) => (
                          <option key={key} value={key} className="text-[#333]">{POLICY_TYPE_LABELS[key]}</option>
                        ))}
                      </select>
                      {chevronIcon}
                    </div>
                  </div>
                  {fieldErrors.pensionType && (
                    <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal mt-sm">{fieldErrors.pensionType}</div>
                  )}
                </div>
              )}
            </div>

            {/* Row 2: Policy status (full width) */}
            {/* Policy status - custom handler to clear sub-fields */}
            <div className="flex flex-col gap-sm">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                What is the policy status? <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <select
                  value={(data.policyStatus as string) || ''}
                  onChange={(e) => {
                    onFieldChange('policyStatus', e.target.value);
                    onClearFieldError('policyStatus');
                    if (e.target.value !== 'CLOSED') {
                      onFieldChange('policyClosedReason', '');
                    }
                  }}
                  className={selectClass('policyStatus')}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Select status...</option>
                  {POLICY_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                {chevronIcon}
              </div>
              {renderFieldError('policyStatus')}
            </div>

            {/* Closed sub-fields */}
            {isClosed && (
              <>
                {/* Closed status */}
                {renderSelectField('policyClosedReason', 'Closed status', CLOSED_REASON_OPTIONS, 'Choose an option')}

                {/* Common closed fields: Provider name + Date of Transfer */}
                {closedReason && (
                  <>
                    <div className="flex flex-row gap-lg">
                      <div className="flex-1">
                        {renderTextField(`${closedLabel}ProviderName`, `Provider name (${closedLabel} to)`)}
                      </div>
                      <div className="flex-1">
                        {renderDateField(`${closedLabel}DateOfTransfer`, 'Date of Transfer', `mask_${closedLabel}_dot`)}
                      </div>
                    </div>

                    {/* Policy reference + Type of Policy */}
                    <div className="flex flex-row gap-lg">
                      <div className="flex-1">
                        {renderTextField(`${closedLabel}PolicyReference`, `Policy reference number (${closedLabel} to)`)}
                      </div>
                      <div className="flex-1">
                        {renderSelectField(`${closedLabel}PolicyType`, `Type of Policy (${closedLabel} to)`, closedPolicyTypeOptions, 'Choose an option')}
                      </div>
                    </div>

                    {/* Type of Transfer + Value as at transfer date */}
                    <div className="flex flex-row gap-lg">
                      <div className="flex-1">
                        {renderSelectField(`${closedLabel}TransferType`, 'Type of Transfer', TRANSFER_TYPE_OPTIONS, 'Choose an option')}
                      </div>
                      <div className="flex-1">
                        {renderCurrencyField(`${closedLabel}TransferValue`, 'Value as at transfer date')}
                      </div>
                    </div>

                    {/* Total Employer + Employee contributions (Transferred only, except ARF/Vested PRSA) */}
                    {(() => {
                      if (isRetired) return null;
                      const pType = (data[`${closedLabel}PolicyType`] as string) || '';
                      const hideContributions = pType === 'ARF' || pType === 'VESTED_PRSA';
                      if (hideContributions) return null;
                      return (
                        <div className="flex flex-row gap-lg">
                          <div className="flex-1">
                            {renderTextField('totalEmployerContributions', 'Total Employer contributions')}
                          </div>
                          <div className="flex-1">
                            {renderTextField('totalEmployeeContributions', 'Total Employee Contributions')}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Retired: BCE + Tax free lump sum fields */}
                    {isRetired && (
                      <>
                        <div className="flex flex-row gap-lg">
                          <div className="flex-1">
                            {renderDateField('retiredBceDate', 'Date of Benefit Crystallisation Event', 'mask_bce')}
                          </div>
                          <div className="flex-1">
                            {renderCurrencyField('retiredBceValue', 'Value of Policy as at BCE')}
                          </div>
                        </div>

                        <div className="flex flex-row gap-lg">
                          <div className="flex flex-col flex-1">
                            <div className="flex flex-col gap-sm flex-1 justify-end">
                              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                                Tax Free Lump Sum <span className="text-[#E7000B]">*</span>
                              </div>
                              <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${fieldErrors.retiredTaxFreeLumpSum ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
                                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={getCurrencyDisplay('retiredTaxFreeLumpSum')}
                                  onChange={(e) => handleCurrencyChange('retiredTaxFreeLumpSum', e.target.value)}
                                  onBlur={() => handleCurrencyBlur('retiredTaxFreeLumpSum')}
                                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                                />
                              </div>
                            </div>
                            {renderFieldError('retiredTaxFreeLumpSum')}
                          </div>
                          <div className="flex flex-col flex-1">
                            <div className="flex flex-col gap-sm flex-1 justify-end">
                              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                                Details of other lump sums on record (€, dates, tax paid and connected policy)
                              </div>
                              <textarea
                                value={(data.retiredPreviousTaxFreeLumpSums as string) || ''}
                                onChange={(e) => onFieldChange('retiredPreviousTaxFreeLumpSums', e.target.value)}
                                className="w-full min-h-[48px] px-md py-[12px] rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary resize-y"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
